import {Button, Hero, useTheme, useWindowSize, type HeadingProps} from '@primer/react-brand/esm'
import {clsx} from 'clsx'
import {useId, useMemo, useRef, useState} from 'react'

import {isBrowser} from '@github-ui/brand-core/utils/platform'
import {OSAwareDownloadActionMenu} from '@github-ui/swp-core/components/OSAwareDownloadActionMenu'
import {useIntersectionObserver} from '@github-ui/swp-core/lib/hooks/use-intersection-observer'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'

import {PlayButton} from '../../../../../brand/components/PlayButton/PlayButton'
import {useVideo} from '../../../hooks/use-video'
import type {Cta, DownloadMenu, PlayButtonLabels, ThemedVisual} from '../../../Index.types'
import {Product, type ProductProps} from '../Product/Product'

import Styles from './Showcase.module.css'

export type ShowcasePrimaryCta = (Cta & {type: 'link'}) | (DownloadMenu & {type: 'download'})

export interface ShowcaseProps {
  title: string
  description: string
  cta1: ShowcasePrimaryCta
  cta2: Cta
  visual: ThemedVisual & {type: 'image' | 'video'}
  a11y: {
    playButton: PlayButtonLabels
  }
}

export const defaultProps: Partial<ShowcaseProps> = {}

// Hero.Heading forwards these props but omits `as` from its public type.
const HEADING_PROPS = {as: 'h2', size: '3', textWrap: 'balance'} satisfies HeadingProps
const BACKGROUND_PROPS = {hasCornerDots: true}
const CTA_ANALYTICS = {context: 'CTAs', location: 'showcase', tag: 'a'}

const PRODUCT_UI = {
  shadowColor: 'blueDark',
  shadowPosition: 'left-bottom',
  roundedCorners: {topRight: false, bottomRight: false},
} satisfies ProductProps['productUI']

const BOTTOM_PRODUCT_UI = {
  ...PRODUCT_UI,
  shadowPosition: 'left',
  roundedCorners: {bottomLeft: false, bottomRight: false},
} satisfies ProductProps['productUI']

export const Showcase = (props: ShowcaseProps) => {
  const initializedProps: ShowcaseProps = {...defaultProps, ...props}
  const {title, description, cta1, cta2, visual, a11y} = initializedProps

  const wrapperRef = useRef<HTMLElement>(null)
  const mediaRef = useRef<HTMLElement>(null)
  const [isVideoReady, setIsVideoReady] = useState(false)

  const titleId = useId()
  const {colorMode} = useTheme()
  const {isSmall: isTablet} = useWindowSize()
  const prefersReducedMotion = usePrefersReducedMotion()
  const {isIntersecting} = useIntersectionObserver(wrapperRef, {threshold: 0.33, isOnce: true})
  const {isIntersecting: isMediaInView} = useIntersectionObserver(mediaRef, {threshold: 0.2, isOnce: true})
  const canAnimateIn = isBrowser() && isIntersecting

  const mediaUrl = colorMode === 'dark' ? visual.url.dark || visual.url.light : visual.url.light

  const actions = props.cta1.type === 'download' ? props.cta1.actions : undefined
  const downloadActions = useMemo(() => actions?.map(({url, ...action}) => ({...action, href: url})) ?? [], [actions])

  const {videoRef, playButtonAction, reportPlayback, togglePlay} = useVideo({
    mediaUrl,
    prefersReducedMotion,
    onReady: () => setIsVideoReady(true),
  })

  return (
    <section ref={wrapperRef} className={Styles.Showcase} aria-labelledby={titleId}>
      <Product
        backgroundProps={BACKGROUND_PROPS}
        mediaPosition={isTablet ? 'bottom-center' : 'center-left'}
        minPadding="var(--showcaseMediaPadding)"
        theme={colorMode}
        productUI={isTablet ? BOTTOM_PRODUCT_UI : PRODUCT_UI}
        shouldAnimateIn
        canAnimateIn={canAnimateIn && isMediaInView && (visual.type !== 'video' || isVideoReady)}
        enterFrom={isTablet ? 'bottom' : 'right'}
        firstElementClass={Styles.Showcase__firstElement}
        firstElement={
          <div
            className={Styles.Showcase__header}
            data-animate-in={!prefersReducedMotion}
            data-can-animate-in={canAnimateIn}
          >
            <Hero align="center" className={Styles.Showcase__hero} aria-labelledby={undefined}>
              <Hero.Heading {...HEADING_PROPS} id={titleId}>
                {title}
              </Hero.Heading>
              <Hero.Description size="300" className={Styles.Showcase__description}>
                {description}
              </Hero.Description>
            </Hero>
            <div className={Styles.Showcase__actions}>
              {cta1.type === 'download' ? (
                <div className={Styles.Showcase__downloadMenu}>
                  <OSAwareDownloadActionMenu
                    actions={downloadActions}
                    menuLabel={cta1.menuLabel}
                    analytics={CTA_ANALYTICS}
                    variant="primary"
                  />
                </div>
              ) : (
                <Button
                  className={Styles.Showcase__action}
                  as="a"
                  href={cta1.url}
                  variant="primary"
                  {...getAnalyticsEvent({...CTA_ANALYTICS, action: cta1.label})}
                >
                  {cta1.label}
                </Button>
              )}

              <Button
                className={Styles.Showcase__action}
                as="a"
                href={cta2.url}
                variant="secondary"
                {...getAnalyticsEvent({...CTA_ANALYTICS, action: cta2.label})}
              >
                {cta2.label}
              </Button>
            </div>
          </div>
        }
      >
        {mediaUrl &&
          (visual.type === 'video' ? (
            <div ref={mediaRef as React.Ref<HTMLDivElement>} className={Styles.Showcase__videoContainer}>
              <video
                ref={videoRef}
                className={clsx(Styles.Showcase__media, Styles.Showcase__video)}
                src={mediaUrl}
                aria-label={visual.alt}
                muted
                playsInline
                preload="metadata"
                onPlay={reportPlayback}
                onPause={reportPlayback}
                onEnded={reportPlayback}
                onEmptied={reportPlayback}
                onLoadedMetadata={reportPlayback}
              />
              {!prefersReducedMotion && (
                <div className={Styles.Showcase__playback}>
                  <PlayButton
                    action={playButtonAction}
                    label={a11y.playButton[playButtonAction]}
                    onClick={togglePlay}
                  />
                </div>
              )}
            </div>
          ) : (
            <img
              ref={mediaRef as React.Ref<HTMLImageElement>}
              className={Styles.Showcase__media}
              src={mediaUrl}
              alt={visual.alt}
              loading="lazy"
            />
          ))}
      </Product>
    </section>
  )
}
